import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslations } from '@lang';

import { Label } from '@common/components/form/Label';
import { RadioGroupField } from '@common/components/RadioGroupField';
import { BlockSelectField } from '@common/components/FormElements/BlockSelectField';
import { genderOptions, genderOptionsWithNeutral } from '@common/constants';
import { maleTitlesOptions, femaleTitlesOptions } from '@utils/guestTitles';

import {
  ForenameColumn,
  GenderColumn,
  GuestCounter,
  GuestRow,
  InputsRow,
  RadioWrapper,
  SurnameColumn,
  TitleColumn,
} from './GuestForm.styles';

import { BlockInputField } from '@common/components/FormElements';

type GuestFormProps = {
  isGenderSpecific: 'M' | 'F' | boolean;
  withLeadGuest: boolean;
  withGenderSelect: boolean;
  showGenderNeutral?: boolean;
  guestIndex: number;
  roomNumber: number;
};

export function GuestForm({
  isGenderSpecific = false,
  withLeadGuest = false,
  withGenderSelect = false,
  showGenderNeutral = false,
  guestIndex,
  roomNumber,
  ...rest
}: GuestFormProps) {
  const { t } = useTranslations();
  const titlesOptions = [
    ...[
      ...(isGenderSpecific === 'M' || isGenderSpecific !== 'F' ? maleTitlesOptions : []),
      ...(isGenderSpecific === 'F' || isGenderSpecific !== 'M' ? femaleTitlesOptions : []),
    ].map((option) => ({
      ...option,
      label: t(option.label),
    })),
  ];
  const form = useFormContext();
  const { control, setValue } = form;

  return (
    <>
      <form {...rest}>
        <fieldset disabled={false}>
          <GuestRow>
            {withLeadGuest && guestIndex === 0 ? (
              <GuestCounter>{t('common.leadGuest')}</GuestCounter>
            ) : (
              <GuestCounter>{t('spa.guestCounter', { counter: guestIndex + 1 })}</GuestCounter>
            )}
            <InputsRow>
              <TitleColumn>
                <Label required>Title</Label>
                <Controller
                  name={`guests.${guestIndex}.guestDetails.title`}
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <BlockSelectField
                      options={titlesOptions}
                      value={titlesOptions.find((title) => title.value === value)}
                      data-testid={`guests-${guestIndex}-title`}
                      placeholder="Title"
                      useNativeDropdownOnMobile={false}
                      onChange={(val) => {
                        onChange(val.value);
                        setValue(`guests.${guestIndex}.roomNumber`, roomNumber);
                        if (val.value === 'Mr')
                          setValue(`guests.${guestIndex}.guestDetails.gender`, 'male');
                        else setValue(`guests.${guestIndex}.guestDetails.gender`, 'female');
                      }}
                    />
                  )}
                />
              </TitleColumn>
              <ForenameColumn>
                <Label required>First Name</Label>
                <Controller
                  name={`guests.${guestIndex}.guestDetails.firstName`}
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <BlockInputField placeholder="First name" value={value} onChange={onChange} />
                  )}
                />
              </ForenameColumn>
              <SurnameColumn>
                <Label required>Last Name</Label>
                <Controller
                  name={`guests.${guestIndex}.guestDetails.lastName`}
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <BlockInputField placeholder="Last name" value={value} onChange={onChange} />
                  )}
                />
              </SurnameColumn>
            </InputsRow>
            {withGenderSelect && (
              <GenderColumn>
                <RadioWrapper>
                  <Controller
                    name={`guests.${guestIndex}.guestDetails.gender`}
                    control={control}
                    render={({ field: { onChange, value } }) => {
                      const options = showGenderNeutral ? genderOptionsWithNeutral : genderOptions;
                      return (
                        <RadioGroupField
                          name={`guests.${guestIndex}.guestDetails.gender`}
                          options={options}
                          input={{
                            value: options.find((opt) => opt.value === value)?.value || '',
                            onChange: (val) => (val ? onChange(val) : null),
                          }}
                          meta={{ error: '', touched: true }}
                          error={false}
                        />
                      );
                    }}
                  />
                </RadioWrapper>
              </GenderColumn>
            )}
          </GuestRow>
        </fieldset>
      </form>
    </>
  );
}
